"use client";

import peterskovsky from "@/videos/peterskovsky.mp4";
import adam from "@/videos/adam.mp4";
import { LANDING_CONTAINER_CLASS } from "@/components/landing/constants";
import Video from "next-video";
import { motion, useInView } from "framer-motion";
import { useCallback, useRef } from "react";
import { useTranslations } from "next-intl";
import { fadeInUp, staggerContainer, videoReveal } from "./animations";
import { trackVideoPlay } from "@/lib/analytics";

const testimonials = [
  { asset: adam, title: "adam" },
  {
    asset: peterskovsky,
    thumbnailTime: 10,
    title: "peterskovsky",
  },
];

export default function TestimonialSection() {
  const t = useTranslations("landing.testimonials");
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: "some" });
  const playedVideos = useRef<Set<string>>(new Set());

  const handleVideoPlay = useCallback((title: string) => {
    if (!playedVideos.current.has(title)) {
      playedVideos.current.add(title);
      trackVideoPlay(title);
    }
  }, []);

  return (
    <section
      ref={sectionRef}
      id="testimonials"
      className="border-b-4 border-black bg-background py-20"
    >
      <motion.div
        className={`${LANDING_CONTAINER_CLASS} space-y-10`}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
      >
        <motion.header
          className="flex items-center justify-between"
          variants={fadeInUp}
        >
          <div className="space-y-4">
            <h2>{t("heading")}</h2>
          </div>
          <div className="hidden gap-3 md:flex">
            <motion.div
              className="h-12 w-12 rounded-full border-2 border-black bg-secondary"
              initial={{ scale: 0 }}
              animate={isInView ? { scale: 1 } : { scale: 0 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
            />
            <motion.div
              className="h-12 w-12 rounded-full border-2 border-black bg-tertiary"
              initial={{ scale: 0 }}
              animate={isInView ? { scale: 1 } : { scale: 0 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 300 }}
            />
            <motion.div
              className="h-12 w-12 rounded-full border-2 border-black bg-accent"
              initial={{ scale: 0 }}
              animate={isInView ? { scale: 1 } : { scale: 0 }}
              transition={{ delay: 0.5, type: "spring", stiffness: 300 }}
            />
          </div>
        </motion.header>

        <div className="flex flex-col md:flex-row gap-6 items-center justify-center">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="w-full md:w-2xl h-auto rounded-2xl overflow-hidden"
              variants={videoReveal}
              transition={{ delay: index * 0.15 }}
            >
              <Video
                className={"w-auto h-auto"}
                src={testimonial.asset}
                thumbnailTime={testimonial.thumbnailTime}
                controls={true}
                muted={false}
                autoplay={false}
                onPlay={() => handleVideoPlay(testimonial.title)}
              />
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
